import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  IonApp,
  IonBadge,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonRouterLink,
  IonRouterOutlet,
  IonSplitPane,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FeatureFlag, Notification } from '@repo/models';
import { addIcons } from 'ionicons';
import {
  barChart,
  cart,
  caretBack,
  caretForward,
  fastFood,
  grid,
  home,
  leaf,
  notifications,
  pricetag,
  restaurant,
  settings,
  star,
  storefront,
  walk,
} from 'ionicons/icons';
import { filter, Subscription } from 'rxjs';
import { NotificationService } from 'src/services/database/supabase/notification-service';
import { RealtimePresenceService } from 'src/services/database/supabase/realtime-presence.service.';
import { LanguageSelectionService } from './language-selection/services/language-selection.service';
import { PosthogService } from 'src/services/posthog.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    RouterLink,
    RouterLinkActive,
    IonApp,
    IonSplitPane,
    IonMenu,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonHeader,
    IonToolbar,
    IonLabel,
    IonRouterLink,
    IonRouterOutlet,
    TranslatePipe,
    IonBadge,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  public currentPage = '';
  public currentLanguage = signal<string>('de');
  public availableLanguages = signal<string[]>([]);
  public showMainLayout = true;
  public readonly isMenuCollapsed = signal(false);

  ngOnInit(): void {
    this.showMainLayout =
      !this.router.url.includes('/login') &&
      !this.router.url.includes('/auth/set-password') &&
      !this.router.url.includes('fullscreen=true');
  }

  private allPages = [
    {
      title: 'PAGE.TITLE.NOTIFICATIONS',
      url: '/notifications',
      icon: 'notifications',
      badge: computed(() => this.unresolvedNotiCount() || null),
    },
    { title: 'PAGE.TITLE.OUTLET', url: '/outlet', icon: 'storefront', testId: 'tables-page' },
    { title: 'PAGE.TITLE.ORDERS', url: '/orders', icon: 'cart', testId: 'orders-page' },
    {
      title: 'PAGE.TITLE.TAKEAWAY_ORDERS',
      url: '/takeaway-orders',
      icon: 'walk',
      testId: 'takeaway-orders-page',
    },
    { title: 'PAGE.TITLE.MENU', url: '/menu', icon: 'restaurant', testId: 'menu-page' },
    {
      title: 'PAGE.TITLE.MENU_OFFERS',
      url: '/menu-offers',
      icon: 'pricetag',
      testId: 'menu-offers-page',
    },
    {
      title: 'PAGE.TITLE.RECOMMENDATIONS',
      url: '/recommendations',
      icon: 'star',
      testId: 'menu-recommendations',
    },
    { title: 'PAGE.TITLE.EXTRAS', url: '/extras', icon: 'fast-food', testId: 'extras-page' },
    { title: 'PAGE.TITLE.ALLERGENS', url: '/allergens', icon: 'leaf', testId: 'allergens-page' },
    { title: 'PAGE.TITLE.CATEGORIES', url: '/categories', icon: 'grid', testId: 'categories-page' },
    {
      title: 'PAGE.TITLE.ANALYTICS',
      url: '/analytics',
      icon: 'bar-chart',
      testId: 'analytics-page',
    },
    {
      title: 'PAGE.TITLE.SETTINGS',
      url: '/settings',
      icon: 'settings',
      testId: 'settings-page',
    },
  ];

  private menuDealsEnabled = signal(false);
  private menuUpgradesEnabled = signal(false);
  private destroyRef = inject(DestroyRef);

  /** Main nav: Menu offers is shown when at least one sub-area (deals or upgrades) is enabled in PostHog. */
  public appPages = computed(() => {
    if (this.menuDealsEnabled() || this.menuUpgradesEnabled()) {
      return this.allPages;
    }
    return this.allPages.filter((page) => page.url !== '/menu-offers');
  });
  public notifications: Notification[] = [];
  public unresolvedNotiCount: Signal<number> = signal(0);
  private healthcheckSubscription: Subscription | null = null;

  constructor(
    private readonly languageSelectionService: LanguageSelectionService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly presenceService: RealtimePresenceService,
    private readonly posthogService: PosthogService
  ) {
    this.posthogService.initPostHog();
    this.initLanguage();
    this.initNotificationSub();
    this.initMenuOffersFeatureFlags();
    addIcons({
      home,
      restaurant,
      cart,
      notifications,
      fastFood,
      leaf,
      grid,
      storefront,
      star,
      barChart,
      settings,
      walk,
      pricetag,
      caretBack,
      caretForward,
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        this.currentPage = url.split('/').filter(Boolean).pop() || '';
        this.showMainLayout =
          url !== '/login' &&
          !url.startsWith('/auth/set-password') &&
          !url.includes('fullscreen=true');
      });
    // Temporarily disable healthcheck
    // this.healthcheckSubscription = this.healthcheckService
    //   .startHeartbeat()
    //   .subscribe((isAlive: boolean) => {
    //     console.log('API is alive:', isAlive);
    //   });
    this.presenceService.subscribeForDevicesOnlineStatusRoom();
  }

  public toggleMenuCollapsed(): void {
    this.isMenuCollapsed.update((collapsed) => !collapsed);
  }

  public setLanguage(language: string) {
    this.currentLanguage.set(language);
    this.translateService.use(language);
  }

  private initLanguage() {
    this.languageSelectionService.initLanguage();
  }

  private initNotificationSub() {
    this.notificationService.startListeningForNotifications();
    this.unresolvedNotiCount = this.notificationService.getUnresolvedNotificationCount();
  }

  private initMenuOffersFeatureFlags() {
    this.posthogService
      .observeFeatureEnabled(FeatureFlag.MenuDealsEnabled)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.menuDealsEnabled.set(enabled);
        this.maybeRedirectIfMenuOffersSectionHidden();
      });

    this.posthogService
      .observeFeatureEnabled(FeatureFlag.MenuUpgradesEnabled)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.menuUpgradesEnabled.set(enabled);
        this.maybeRedirectIfMenuOffersSectionHidden();
      });
  }

  /** If both deals and upgrades are off, user cannot use Menu offers — leave the area. */
  private maybeRedirectIfMenuOffersSectionHidden(): void {
    if (this.menuDealsEnabled() || this.menuUpgradesEnabled()) {
      return;
    }
    const path = this.router.url.split('?')[0];
    if (path.startsWith('/menu-offers')) {
      void this.router.navigate(['/notifications'], { replaceUrl: true });
    }
  }

  ngOnDestroy(): void {
    if (this.healthcheckSubscription != null) {
      this.healthcheckSubscription.unsubscribe();
    }
  }
}
